import useLocalStorageListener from "hooks/useLocalStorageListener";
import { t } from "i18next";
import { useMemo } from "react";
import type { TPortalType } from "store/types";

import type { TButtonMenuOption } from "../../ButtonOptionsMenu";

/**
 * Add new table types for conditional columns here ..
 */
export type TableType = "management-general-table" | "press-management-table";

/**
 * Get view columns options, used with
 * <LastElementTable/> and <ButtonOptionsMenu/>
 */
export const getColumnViewOptions = (type: TableType, portalType: TPortalType): TButtonMenuOption[] => {
    const columns: Record<TableType, TButtonMenuOption[]> = {
        "management-general-table": [
            { id: "title", label: t("Works.WorkLabel"), disabled: true },
            { id: "author", label: t("Management.Author"), disabled: true },
            { id: "balancePrev", label: t("Management.BalancePrev"), disabled: true },
            { id: "advanceAmount", label: t("Management.AdvanceAmount"), disabled: true },
            { id: "quantity", label: t("Management.Quantity") },
            { id: "additionalRights", label: t("Management.AdditionalRights") },
            { id: "audiovisualAdaption", label: t("Management.AudiovisualAdaption") },
            { id: "collectiveManagement", label: t("Management.CollectiveManagement") },
            { id: "other", label: t("Management.Other") },
            { id: "grossDuty", label: t("Management.GrossDuty"), disabled: true },
            { id: "balance", label: t("Management.Balance"), disabled: true },
        ],
        "press-management-table": [
            { id: "titleName", label: t(`PressAudio.Management.Publication_${portalType}`), disabled: true },
            {
                id: "beneficiary",
                label: `${t("PressAudio.Management.Beneficiary")}/${t(`PressAudio.Management.Article_${portalType}`)}`,
                disabled: true,
            },
            { id: "contractSigned", label: t("PressAudio.Management.Contract"), disabled: true },
            { id: "grossDuty", label: t("PressAudio.Management.GrossDuty") },
            { id: "netToPay", label: t("PressAudio.Management.NetToPay") },
            { id: "paymentDate", label: t("Articles.PaymentDate") },
            { id: "paidStatus", label: t("PressAudio.Management.Payment"), disabled: true },
            { id: "completionStatus", label: t("Articles.CompletionStatusLabel"), disabled: true },
        ],
        // Add new tables conditionnal columns here ..
    };
    return columns[type];
};

/** Columns marked `disabled` are always visible and checked; user prefs only apply to optional columns. */
export function mergeColumnViewWithRequiredOptions(
    storedOptions: TButtonMenuOption[] | null,
    defaultOptions: TButtonMenuOption[],
): TButtonMenuOption[] {
    const requiredOptions = defaultOptions.filter((option) => option.disabled);
    const optionalDefaults = defaultOptions.filter((option) => !option.disabled);

    if (!storedOptions) {
        return defaultOptions;
    }

    const storedIds = new Set(storedOptions.map((option) => option.id));
    const optionalChosen = optionalDefaults.filter((option) => storedIds.has(option.id));

    return [...requiredOptions, ...optionalChosen];
}

/**
 * Reusable hook to get Set of column view ids based on tableType
 * @param {TableType} tableType type of table
 * @param {TPortalType} portalType
 * @returns {Set<string | number>} Set of column view IDs
 */
export const useTableColumnView = (tableType: TableType, portalType: TPortalType) => {
    const defaultColumnView = getColumnViewOptions(tableType, portalType);
    const storedColumnView = useLocalStorageListener<TButtonMenuOption[] | null>(`column_view:${tableType}`);
    const columnViewOptions = mergeColumnViewWithRequiredOptions(storedColumnView, defaultColumnView);

    return useMemo(() => {
        const requiredColumnIds = new Set(
            defaultColumnView.filter((option) => option.disabled).map((option) => option.id),
        );
        const columnViewIds = new Set<string | number>(columnViewOptions.map((item) => item.id));
        const columnsToCheck = new Set<string | number>(defaultColumnView.map((item) => item.id));

        const showColumn = (id) => {
            if (requiredColumnIds.has(id)) {
                return true;
            }
            if (columnsToCheck.has(id)) {
                return columnViewIds.has(id);
            }
            return true;
        };

        return {
            // Choosen columns to show
            columnViewIds,
            // Columns that needs a check before a conditionnal render
            columnsToCheck,
            // Show columns method that returns boolean
            showColumn,
        };
    }, [columnViewOptions, defaultColumnView]);
};
